import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  CircularProgress,
  Switch,
  FormControlLabel,
  MenuItem,
  IconButton,
  Typography,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../DataLayouts/Header";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import axios from "axios";
import { MAIN_URL } from "../../../Configurations/Urls";
import { fetchPayrollComponentTypes } from "../../../Apis/Payroll";

const MAX_INCREMENT = 999999999.99;

const EMPTY_FORM = {
  organization_payroll_component_type_id: "",
  payroll_component_name: "",
  calculation_method: "",
  fixed_amount: "",
  percentage_of_component: "",
  formula_json: "",
  taxable: false,
  affects_net_pay: false,
  rounding_rule: "",
  rounding_precision: "",
  sort_order: "",
  is_active: true,
  effective_from: "",
  effective_to: "",
};

const clearCalculationFields = (method) => {
  const cleared = {
    fixed_amount: "",
    percentage_of_component: "",
    formula_json: "",
  };

  switch (method) {
    case "Fixed":
      delete cleared.fixed_amount;
      break;
    case "Percentage":
      delete cleared.percentage_of_component;
      break;
    case "Formula":
      delete cleared.formula_json;
      break;
    default:
      break;
  }

  return cleared;
};

const EMPTY_SLAB = {
  organization_payroll_component_slab_id: null,
  slab_min: "",
  slab_max: "",
  slab_value_type: "",
  slab_value: "",
};

const buildPayload = (data, orgId, componentId = null, componentSlabs = []) => {
  const payload = {
    organization_id: orgId,
    organization_payroll_component_type_id: data.organization_payroll_component_type_id,
    payroll_component_name: data.payroll_component_name,
    calculation_method: data.calculation_method,
    taxable: !!data.taxable,
    affects_net_pay: !!data.affects_net_pay,
    rounding_rule: data.rounding_rule,
    rounding_precision: data.rounding_precision !== "" ? Number(data.rounding_precision) : null,
    sort_order: data.sort_order !== "" ? Number(data.sort_order) : null,
    is_active: data.is_active !== false && data.is_active !== "0",
    effective_from: data.effective_from,
    effective_to: data.effective_to,
    fixed_amount: null,
    percentage_of_component: null,
    formula_json: null,
  };

  if (componentId) {
    payload.organization_payroll_component_id = componentId;
  }

  switch (data.calculation_method) {
    case "Fixed":
      payload.fixed_amount = data.fixed_amount !== "" ? Number(data.fixed_amount) : null;
      break;
    case "Percentage":
      payload.percentage_of_component = data.percentage_of_component || null;
      break;
    case "Formula":
      payload.formula_json = data.formula_json || null;
      break;
    case "Slab":
      payload.component_slabs = componentSlabs.map((slab) => ({
        organization_payroll_component_slab_id: slab.organization_payroll_component_slab_id || null,
        slab_min: slab.slab_min !== "" ? Number(slab.slab_min) : null,
        slab_max: slab.slab_max !== "" ? Number(slab.slab_max) : null,
        slab_value_type: slab.slab_value_type || null,
        slab_value: slab.slab_value !== "" ? Number(slab.slab_value) : null,
      }));
      break;

    default:
      break;
  }

  return payload;
};

function ComponentForm({ mode }) {
  const { id } = useParams();
  const { userData } = useAuthStore();
  const org = userData?.organization;

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [componentSlabs, setComponentSlabs] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(mode === "edit");
  const [btnLoading, setbtnLoading] = useState(false);
  const [ComponentTypes, setComponentTypes] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPayrollComponentTypes(org?.organization_id)
      .then((data) => {
        setComponentTypes(data?.payrollTypes);
      })
      .catch((err) => {
        setFormErrors({ general: err.message });
      });
  }, [org?.organization_id]);

  useEffect(() => {
    const getdataById = async () => {
      const response = await axios.get(
        `${MAIN_URL}/api/organizations/${org.organization_id}/payroll-component/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const record = response?.data?.payroll || {};
      setFormData({
        organization_payroll_component_type_id: record.organization_payroll_component_type_id ?? "",
        payroll_component_name: record.payroll_component_name || "",
        calculation_method: record.calculation_method || "",
        fixed_amount: record.fixed_amount ?? "",
        percentage_of_component: record.percentage_of_component || "",
        formula_json: record.formula_json || "",
        taxable: record.taxable === 1 || record.taxable === true,
        affects_net_pay: record.affects_net_pay === 1 || record.affects_net_pay === true,
        rounding_rule: record.rounding_rule || "",
        rounding_precision: record.rounding_precision ?? "",
        sort_order: record.sort_order ?? "",
        is_active: record.is_active !== 0 && record.is_active !== false,
        effective_from: record.effective_from ? String(record.effective_from).slice(0, 10) : "",
        effective_to: record.effective_to ? String(record.effective_to).slice(0, 10) : "",
      });
      const slabs = (record.component_slabs || []).map((slab) => ({
        organization_payroll_component_slab_id: slab.organization_payroll_component_slab_id ?? null,
        slab_min: slab.slab_min ?? "",
        slab_max: slab.slab_max ?? "",
        slab_value_type: slab.slab_value_type || "",
        slab_value: slab.slab_value ?? "",
      }));
      setComponentSlabs(
        slabs.length > 0
          ? slabs
          : record.calculation_method === "Slab"
            ? [{ ...EMPTY_SLAB }]
            : []
      );
      setLoading(false);
    };

    if ((mode === "edit" || mode === "view") && id) {
      setLoading(true);
      getdataById();
    }
  }, [mode, id, org?.organization_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "calculation_method") {
      setFormData((prev) => ({
        ...prev,
        calculation_method: value,
        ...clearCalculationFields(value),
      }));
      if (value === "Slab") {
        setComponentSlabs((prev) => (prev.length > 0 ? prev : [{ ...EMPTY_SLAB }]));
      } else {
        setComponentSlabs([]);
      }
      setFormErrors((prev) => ({
        ...prev,
        calculation_method: "",
        fixed_amount: "",
        percentage_of_component: "",
        formula_json: "",
        component_slabs: "",
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSlabChange = (index, field, value) => {
    setComponentSlabs((prev) =>
      prev.map((slab, i) => (i === index ? { ...slab, [field]: value } : slab))
    );
    setFormErrors((prev) => ({
      ...prev,
      [`component_slabs.${index}.${field}`]: "",
      component_slabs: "",
    }));
  };

  const addSlabRow = () => {
    setComponentSlabs((prev) => [...prev, { ...EMPTY_SLAB }]);
  };

  const removeSlabRow = (index) => {
    setComponentSlabs((prev) => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.payroll_component_name?.trim()) {
      errors.payroll_component_name = "Payroll component name is required.";
    }

    if (!formData.organization_payroll_component_type_id) {
      errors.organization_payroll_component_type_id = "Payroll component type is required.";
    }

    if (!formData.calculation_method) {
      errors.calculation_method = "Calculation method is required.";
    }

    if (!formData.rounding_rule) {
      errors.rounding_rule = "Rounding rule is required.";
    }

    if (!formData.effective_from) {
      errors.effective_from = "Start date is required.";
    }

    if (!formData.effective_to) {
      errors.effective_to = "End date is required.";
    } else if (formData.effective_from && formData.effective_to < formData.effective_from) {
      errors.effective_to = "End date must be on or after start date.";
    }

    switch (formData.calculation_method) {
      case "Fixed":
        if (formData.fixed_amount === "" || formData.fixed_amount == null) {
          errors.fixed_amount = "Fixed amount is required.";
        }
        break;
      case "Percentage":
        if (!formData.percentage_of_component?.trim()) {
          errors.percentage_of_component = "Percentage of component is required.";
        }
        break;
      case "Formula":
        if (!formData.formula_json?.trim()) {
          errors.formula_json = "Formula JSON is required.";
        } else {
          try {
            JSON.parse(formData.formula_json);
          } catch {
            errors.formula_json = "Formula JSON must be valid JSON.";
          }
        }
        break;
      case "Slab":
        if (componentSlabs.length === 0) {
          errors.component_slabs = "At least one component slab is required.";
        }
        componentSlabs.forEach((slab, index) => {
          if (slab.slab_min === "" || slab.slab_min == null) {
            errors[`component_slabs.${index}.slab_min`] = "Min slab is required.";
          }
          if (slab.slab_max === "" || slab.slab_max == null) {
            errors[`component_slabs.${index}.slab_max`] = "Max slab is required.";
          } else if (
            slab.slab_min !== "" &&
            Number(slab.slab_max) < Number(slab.slab_min)
          ) {
            errors[`component_slabs.${index}.slab_max`] =
              "Max slab must be greater than or equal to min slab.";
          }
          if (!slab.slab_value_type) {
            errors[`component_slabs.${index}.slab_value_type`] = "Slab value type is required.";
          }
          if (slab.slab_value === "" || slab.slab_value == null) {
            errors[`component_slabs.${index}.slab_value`] = "Slab value is required.";
          }
        });
        break;
      default:
        break;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setbtnLoading(true);

    const payload = buildPayload(formData, org.organization_id, mode === "edit" ? id : null, componentSlabs);

    try {
      if (mode === "edit") {
        await axios.put(
          `${MAIN_URL}/api/organizations/${org.organization_id}/payroll-component/${id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      } else {
        await axios.post(
          `${MAIN_URL}/api/organizations/${org.organization_id}/payroll-component`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }
      toast.success(
        mode === "edit" ? "Payroll Component updated!" : "Payroll Component created!"
      );
      setFormErrors({});
      navigate(-1);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        setFormErrors(validationErrors);
        const errorMessages = Object.values(validationErrors)
          .map((arr) => arr[0])
          .join(" ");
        toast.error(errorMessages || "Validation failed.");
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setbtnLoading(false);
    }
  };

  const renderCalculationFields = () => {
    switch (formData.calculation_method) {
      case "Fixed":
        return (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Fixed Amount"
              name="fixed_amount"
              type="number"
              value={formData.fixed_amount}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*\.?\d*$/.test(value)) {
                  if (value === "" || Number(value) <= MAX_INCREMENT) {
                    handleChange(e);
                  }
                }
              }}
              error={!!formErrors.fixed_amount}
              helperText={formErrors.fixed_amount}
              required
              disabled={mode === "view"}
              inputProps={{ min: 0, max: MAX_INCREMENT }}
            />
          </Grid>
        );
      case "Percentage":
        return (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Percentage of Component"
              name="percentage_of_component"
              value={formData.percentage_of_component}
              onChange={handleChange}
              error={!!formErrors.percentage_of_component}
              helperText={formErrors.percentage_of_component || "e.g. Basic or 10"}
              required
              disabled={mode === "view"}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>
        );
      case "Formula":
        return (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Formula JSON"
              name="formula_json"
              value={formData.formula_json}
              onChange={handleChange}
              error={!!formErrors.formula_json}
              helperText={formErrors.formula_json}
              required
              disabled={mode === "view"}
              multiline
              rows={6}
            />
          </Grid>
        );
      case "Slab":
        return (
          <Grid item xs={12}>
            <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Component Slabs
              </Typography>
              {mode !== "view" && (
                <Button startIcon={<AddIcon />} onClick={addSlabRow} size="small">
                  Add Slab
                </Button>
              )}
            </Box>
            {formErrors.component_slabs && (
              <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                {formErrors.component_slabs}
              </Typography>
            )}
            {componentSlabs.map((slab, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Slab {index + 1}
                  </Typography>
                  {mode !== "view" && componentSlabs.length > 1 && (
                    <IconButton size="small" color="error" onClick={() => removeSlabRow(index)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Min Slab"
                      type="number"
                      value={slab.slab_min}
                      onChange={(e) => handleSlabChange(index, "slab_min", e.target.value)}
                      error={!!formErrors[`component_slabs.${index}.slab_min`]}
                      helperText={formErrors[`component_slabs.${index}.slab_min`]}
                      required
                      disabled={mode === "view"}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Max Slab"
                      type="number"
                      value={slab.slab_max}
                      onChange={(e) => handleSlabChange(index, "slab_max", e.target.value)}
                      error={!!formErrors[`component_slabs.${index}.slab_max`]}
                      helperText={formErrors[`component_slabs.${index}.slab_max`]}
                      required
                      disabled={mode === "view"}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Slab Value Type"
                      value={slab.slab_value_type}
                      onChange={(e) => handleSlabChange(index, "slab_value_type", e.target.value)}
                      error={!!formErrors[`component_slabs.${index}.slab_value_type`]}
                      helperText={formErrors[`component_slabs.${index}.slab_value_type`]}
                      required
                      disabled={mode === "view"}
                    >
                      <MenuItem value="Fixed">Fixed</MenuItem>
                      <MenuItem value="Percentage">Percentage</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Slab Value"
                      type="number"
                      value={slab.slab_value}
                      onChange={(e) => handleSlabChange(index, "slab_value", e.target.value)}
                      error={!!formErrors[`component_slabs.${index}.slab_value`]}
                      helperText={formErrors[`component_slabs.${index}.slab_value`]}
                      required
                      disabled={mode === "view"}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                </Grid>
                {index < componentSlabs.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Paper>
            ))}
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Header
      mode={mode}
      updateMessage={"Payroll Component"}
      addMessage={"Component"}
      homeLink={"/payroll/components"}
      homeText={"Payroll Component"}
      onSubmit={handleSubmit}
      loading={loading}
      btnLoading={btnLoading}
    >
      {loading ? (
        <Grid container spacing={2}>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper elevation={4} sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Component Type"
                    name="organization_payroll_component_type_id"
                    value={formData.organization_payroll_component_type_id}
                    onChange={handleChange}
                    error={!!formErrors.organization_payroll_component_type_id}
                    helperText={formErrors.organization_payroll_component_type_id}
                    required
                    disabled={mode === "view"}
                  >
                    {ComponentTypes?.map((option) => (
                      <MenuItem
                        key={option.organization_payroll_component_type_id}
                        value={option.organization_payroll_component_type_id}
                      >
                        {option?.payroll_component_type_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Component Name"
                    name="payroll_component_name"
                    value={formData.payroll_component_name}
                    onChange={handleChange}
                    error={!!formErrors.payroll_component_name}
                    helperText={formErrors.payroll_component_name}
                    inputProps={{ maxLength: 50 }}
                    required
                    disabled={mode === "view"}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Calculation Method"
                    name="calculation_method"
                    value={formData.calculation_method}
                    onChange={handleChange}
                    error={!!formErrors.calculation_method}
                    helperText={formErrors.calculation_method}
                    required
                    disabled={mode === "view"}
                  >
                    <MenuItem value="Fixed">Fixed</MenuItem>
                    <MenuItem value="Percentage">Percentage</MenuItem>
                    <MenuItem value="Formula">Formula</MenuItem>
                    <MenuItem value="Slab">Slab</MenuItem>
                  </TextField>
                </Grid>

                {renderCalculationFields()}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.taxable}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            taxable: e.target.checked,
                          }))
                        }
                        color="primary"
                        disabled={mode === "view"}
                      />
                    }
                    label="Taxable"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Rounding Rule"
                    name="rounding_rule"
                    value={formData.rounding_rule}
                    onChange={handleChange}
                    error={!!formErrors.rounding_rule}
                    helperText={formErrors.rounding_rule}
                    required
                    disabled={mode === "view"}
                  >
                    <MenuItem value="None">None</MenuItem>
                    <MenuItem value="Nearest">Nearest</MenuItem>
                    <MenuItem value="Up">Up</MenuItem>
                    <MenuItem value="Down">Down</MenuItem>
                  </TextField>
                </Grid>

                {formData.rounding_rule && formData.rounding_rule !== "None" && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Rounding Precision"
                      name="rounding_precision"
                      type="number"
                      value={formData.rounding_precision}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*$/.test(value)) {
                          if (value === "" || Number(value) <= 127) {
                            handleChange(e);
                          }
                        }
                      }}
                      error={!!formErrors.rounding_precision}
                      helperText={formErrors.rounding_precision}
                      disabled={mode === "view"}
                      inputProps={{ min: 0, max: 127 }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.affects_net_pay}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            affects_net_pay: e.target.checked,
                          }))
                        }
                        color="primary"
                        disabled={mode === "view"}
                      />
                    }
                    label="Affects net pay"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Sort Order"
                    name="sort_order"
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value)) {
                        handleChange(e);
                      }
                    }}
                    error={!!formErrors.sort_order}
                    helperText={formErrors.sort_order}
                    disabled={mode === "view"}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Effective From"
                    name="effective_from"
                    type="date"
                    value={formData.effective_from}
                    onChange={handleChange}
                    error={!!formErrors.effective_from}
                    helperText={formErrors.effective_from}
                    required
                    disabled={mode === "view"}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Effective To"
                    name="effective_to"
                    type="date"
                    value={formData.effective_to}
                    onChange={handleChange}
                    error={!!formErrors.effective_to}
                    helperText={formErrors.effective_to}
                    required
                    disabled={mode === "view"}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {mode !== "view" && (
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={handleSubmit}
                      disabled={loading || btnLoading}
                      sx={{
                        borderRadius: 2,
                        minWidth: 120,
                        textTransform: "capitalize",
                        fontWeight: 500,
                      }}
                    >
                      {btnLoading ? (
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </Grid>
                )}

                {mode === "edit" && (
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={() => navigate(-1)}
                      sx={{
                        borderRadius: 2,
                        minWidth: 120,
                        textTransform: "capitalize",
                        fontWeight: 500,
                        backgroundColor: "#1976d2",
                        "&:hover": { backgroundColor: "#115293" },
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Header>
  );
}

export default ComponentForm;
